import {
    Card,
    CardHeader,
    FormControl,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Typography
} from '@mui/material';
import {
    DataGrid,
    gridPageCountSelector,
    gridPageSelector,
    koKR,
    useGridApiContext,
    useGridSelector
} from '@mui/x-data-grid';
import { Fragment, useEffect, useState } from 'react';
import { apiService } from '../../api/axiosClient';
import CustomAvatar from '../../components/mui/avatar';
import Spinner from '../../components/spinner';
import CustomNoRowsOverlay from '../../utils/CustomNoRowsOverlay';
import { getInitials } from '../../utils/get-initials';

const renderClient = params => {
    const { row } = params;
    const stateNum = Math.floor(Math.random() * 6);
    const states = ['success', 'error', 'warning', 'info', 'primary', 'secondary'];
    const color = states[stateNum];
  
    return (
      <CustomAvatar skin='light' color={color} sx={{ mr: 3, fontSize: '.8rem', width: '1.875rem', height: '1.875rem' }}>
        {getInitials(row.name ? row.name : 'User')}
      </CustomAvatar>
    )
}

const FindCompany = (props) => {
    const {field='com_name', q, reload, setReload, selected, setSelected} = props;
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [total, setTotal] = useState(0);
    const [sort, setSort] = useState('asc');
    const [pageSize, setPageSize] = useState(10);
    const [list, setList] = useState([]);
    const [searchValue, setSearchValue] = useState(q);
    const [sortColumn, setSortColumn] = useState('com_name');
    const [selectionModel, setSelectionModel] = useState([]);

    const loadCompany = async (sort, query, field, column) => {
        try {
            const response = await apiService.get('/users/company/search', {
                params: {
                    page,
                    pageSize,
                    query,
                    field,
                    sort,
                    column
                }
            })
            const payload = response.data;
            
            setTotal(payload.size)
            setList(payload.list)
            setLoading(false)
        } catch (e) {
            console.error(e)
        }
    }

    useEffect(() => {
        loadCompany(sort, searchValue, field, sortColumn)
    }, [page, pageSize, reload]);

    const handleSortModel = (newModel) => {
        if (newModel.length) {
            setSort(newModel[0].sort)
            setSortColumn(newModel[0].field)
            loadCompany(newModel[0].sort, searchValue, field, newModel[0].field)
        } else {
            setSort('asc')
            setSortColumn('com_name')
        }
    }

    const handleSearch = value => {
        setSearchValue(value)
        loadCompany(sort, value, field, sortColumn)
    }

    const onPageSizeChange = e => {
        setPageSize(e.target.value)
    }

    function CustomPagination() {
        const apiRef = useGridApiContext();
        const _page = useGridSelector(apiRef, gridPageSelector);
        const pageCount = useGridSelector(apiRef, gridPageCountSelector);
        
        return (
            <Stack direction="row" spacing={2} sx={{ pr: 4, alignItems: 'center' }}>
                <InputLabel id="pagesize-select">행/페이지</InputLabel>
                <FormControl variant="standard" sx={{ mr: 1, minWidth: 70 }}>
                    <Select
                        labelId="pagesize-select"
                        id="pagesize-select"
                        value={pageSize}
                        onChange={onPageSizeChange}
                        label="pageSize"
                    >
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                    </Select>
                </FormControl>
                <Pagination
                    color="primary"
                    count={pageCount}
                    page={_page + 1}
                    onChange={(event, value) => apiRef.current.setPage(value - 1)}
                />
            </Stack>
        );
    }

    const columns = [
        {
            field: '__check__',
            hideable: false,
            sortable: false,
            filterable: false,
            headerAlign: 'left'
        },
        {
            minWidth: 220,
            field: 'com_name',
            hideable: false,
            headerName: '업체명',
            renderCell: params => (
                <Fragment>
                    {renderClient(params)}
                    <Typography variant='body2' sx={{ ml: 1, color: 'text.primary' }}>
                        {params.row.com_name}
                    </Typography>
                </Fragment>
            )
        },
        {
            minWidth: 200,
            field: 'com_code',
            hideable: false,
            headerName: '사업자번호',
            renderCell: params => (
                <Typography variant='body2' sx={{ color: 'text.primary' }}>
                    {params.row.com_code}
                </Typography>
            )
        },
        {
            minWidth: 100,
            field: 'com_sector',
            hideable: false,
            headerName: '업태',
            renderCell: params => (
                <Typography variant='body2' sx={{ color: 'text.primary' }}>
                    {params.row.com_sector}
                </Typography>
            )
        },
        {
            minWidth: 100,
            field: 'com_field',
            hideable: false,
            headerName: '종목',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {params.row.com_field}
            </Typography>
            )
        },
        {
            minWidth: 100,
            field: 'com_ackdate',
            hideable: false,
            headerName: '승인',
            renderCell: params => (
            <Typography variant='body2' sx={{ color: 'text.primary' }}>
                {params.row.com_ackdate ? '승인': '미승인'}
            </Typography>
            )
        }        
    ]

    return (
        loading ? <Spinner/> :
        <Card>
            <CardHeader title='회사 리스트' />
            <DataGrid
                sx={{
                    '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': { display: 'none'},
                    ...(list.length == 0 && { '& .MuiDataGrid-virtualScrollerContent': {height: '200px !important' } })
                }}
                localeText={koKR.components.MuiDataGrid.defaultProps.localeText}
                autoHeight
                getRowId={row => row.com_id}
                pagination
                rows={list}
                rowCount={total || 0}
                columns={columns}
                pageSize={pageSize}
                sortingMode='server'
                paginationMode='server'
                onSortModelChange={handleSortModel}
                onPageChange={newPage => setPage(newPage)}
                components={{
                    Pagination: CustomPagination,
                    NoRowsOverlay: CustomNoRowsOverlay,
                }}
                componentsProps={{
                    toolbar: {
                        value: searchValue,
                        clearSearch: () => handleSearch(''),
                        onChange: event => handleSearch(event.target.value),
                        numSelected: selected.length,
                        selected,
                        reload,
                        setReload
                    }
                }}
                checkboxSelection={true}
                disableSelectionOnClick={true}
                selectionModel={selectionModel}
                onSelectionModelChange={(selection) => {
                    const selectionSet = new Set(selectionModel);
                    const result = selection.filter((s) => !selectionSet.has(s));
                    setSelectionModel(result);

                    list.find((item) => {
                        if (item.com_name === result[0]) {
                            setSelected(item);
                        }
                    })
                }}
            />
        </Card>
  )
}

export default FindCompany
